import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
    { id: "smartcaddy", label: "SmartCaddy (smart cart family)" },
    { id: "retail_suite", label: "Smart retail suite" },
    { id: "retail_media", label: "Retail media network" },
    { id: "full_platform", label: "Full ReTechler platform" },
    { id: "other", label: "Something else" },
];

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        role: "",
        phone: "",
        interest: "smartcaddy",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.company) {
            toast.error("Please fill in name, email and company.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/demo-request`, form);
            setSubmitted(true);
            toast.success("Thanks — our team will be in touch within one business day.");
            setForm({
                name: "",
                email: "",
                company: "",
                role: "",
                phone: "",
                interest: "smartcaddy",
                message: "",
            });
        } catch (err) {
            console.error(err);
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Could not submit. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Could not submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative bg-rt-bg py-24 lg:py-36 border-t border-white/10"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="rt-eyebrow mb-4">Contact / Request a demo</div>
                        <h2 className="rt-h2 text-4xl md:text-5xl lg:text-6xl text-white">
                            See your store<br />
                            <span className="italic font-light text-rt-teal">running on ReTechler.</span>
                        </h2>
                        <p className="mt-6 text-rt-text2 text-base lg:text-lg max-w-md leading-relaxed">
                            Tell us about your portfolio. We'll prepare a tailored walkthrough of
                            SmartCaddy, the automation suite and a real ROI model for your floor plan.
                        </p>

                        <div className="mt-10 space-y-5 text-sm">
                            <div className="flex items-center gap-3 text-rt-text2">
                                <Mail className="w-4 h-4 text-rt-teal shrink-0" />
                                <a href="mailto:contact@retechler.com" className="hover:text-white">
                                    contact@retechler.com
                                </a>
                            </div>
                            <div className="flex items-center gap-3 text-rt-text2">
                                <Globe className="w-4 h-4 text-rt-teal shrink-0" />
                                <a
                                    href="https://smartcaddy.store/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-white"
                                >
                                    smartcaddy.store · live product
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        {submitted ? (
                            <div
                                data-testid="contact-success"
                                className="border border-rt-teal/40 bg-rt-teal/5 p-10 lg:p-14"
                            >
                                <div className="rt-eyebrow mb-3">Received · {new Date().toLocaleDateString()}</div>
                                <h3 className="font-heading text-3xl text-white mb-3">
                                    Thanks — your request is logged.
                                </h3>
                                <p className="text-rt-text2 max-w-lg">
                                    A solutions architect will reach out within one business day to
                                    schedule your tailored walkthrough.
                                </p>
                                <button
                                    onClick={() => setSubmitted(false)}
                                    data-testid="contact-submit-another"
                                    className="mt-6 text-sm border border-white/20 px-4 py-2 hover:bg-white/5"
                                >
                                    Submit another request
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                noValidate
                                data-testid="contact-form"
                                className="border border-white/10 bg-rt-surface p-8 lg:p-10 space-y-5"
                            >
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <Field label="Full name *" testid="contact-input-name">
                                        <input
                                            type="text"
                                            data-testid="contact-input-name"
                                            value={form.name}
                                            onChange={update("name")}
                                            required
                                            className="rt-input"
                                            placeholder="Anika Mehta"
                                        />
                                    </Field>
                                    <Field label="Work email *" testid="contact-input-email">
                                        <input
                                            type="email"
                                            data-testid="contact-input-email"
                                            value={form.email}
                                            onChange={update("email")}
                                            required
                                            className="rt-input"
                                            placeholder="team@retailer.com"
                                        />
                                    </Field>
                                    <Field label="Company *" testid="contact-input-company">
                                        <input
                                            type="text"
                                            data-testid="contact-input-company"
                                            value={form.company}
                                            onChange={update("company")}
                                            required
                                            className="rt-input"
                                            placeholder="Westfield Group"
                                        />
                                    </Field>
                                    <Field label="Role" testid="contact-input-role">
                                        <input
                                            type="text"
                                            data-testid="contact-input-role"
                                            value={form.role}
                                            onChange={update("role")}
                                            className="rt-input"
                                            placeholder="VP Operations"
                                        />
                                    </Field>
                                    <Field label="Phone" testid="contact-input-phone">
                                        <input
                                            type="tel"
                                            data-testid="contact-input-phone"
                                            value={form.phone}
                                            onChange={update("phone")}
                                            className="rt-input"
                                            placeholder="+1 415 ..."
                                        />
                                    </Field>
                                    <Field label="I'm interested in" testid="contact-input-interest">
                                        <select
                                            data-testid="contact-input-interest"
                                            value={form.interest}
                                            onChange={update("interest")}
                                            className="rt-input"
                                        >
                                            {INTERESTS.map((i) => (
                                                <option key={i.id} value={i.id} className="bg-rt-bg">
                                                    {i.label}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                                <Field label="Project notes" testid="contact-input-message">
                                    <textarea
                                        data-testid="contact-input-message"
                                        value={form.message}
                                        onChange={update("message")}
                                        rows={4}
                                        className="rt-input resize-none"
                                        placeholder="Number of stores, current POS, timeline…"
                                    />
                                </Field>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="contact-submit"
                                    className="inline-flex items-center gap-2 bg-rt-teal text-black font-medium px-6 py-3.5 hover:bg-rt-tealDim transition-colors disabled:opacity-60"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                                        </>
                                    ) : (
                                        <>
                                            Request demo <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>

                                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-rt-text3">
                                    By submitting, you agree to our privacy notice. We never share data with third parties.
                                </p>

                                <style>{`
                                    .rt-input {
                                        width: 100%;
                                        background: #0A0A0B;
                                        border: 1px solid #2D2D30;
                                        color: #fff;
                                        padding: 12px 14px;
                                        font-size: 14px;
                                        font-family: 'Manrope', sans-serif;
                                        outline: none;
                                        transition: border-color .2s, box-shadow .2s;
                                    }
                                    .rt-input:focus {
                                        border-color: #2DD4BF;
                                        box-shadow: 0 0 0 2px rgba(45,212,191,0.2);
                                    }
                                    .rt-input::placeholder { color: #71717A; }
                                `}</style>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-rt-text3 mb-2">
                {label}
            </div>
            {children}
        </label>
    );
}
